import type { CSSProperties } from 'react';
import { PortalLink } from '@akademi/site-builder-runtime';
import styles from './TradeCounterHome.module.css';

export interface TradeCounterCategory {
  label: string;
  href: string;
  image?: string;
}

export interface TradeCounterOffer {
  eyebrow?: string;
  title: string;
  price?: string;
  previousPrice?: string;
  saving?: string;
  href: string;
  image?: string;
}

export interface TradeCounterHomeProps {
  title?: string;
  description?: string;
  heroImage?: string;
  heroHref?: string;
  heroCta?: string;
  categories?: TradeCounterCategory[];
  deal?: TradeCounterOffer;
  offers?: TradeCounterOffer[];
  quickOrderHref?: string;
  catalogueHref?: string;
}

export function TradeCounterHome({
  title = 'Everything for the job, ready when you are',
  description = 'Heating, plumbing and engineering supplies from stock, with clear trade pricing.',
  heroImage,
  heroHref = '/categories',
  heroCta = 'Browse all products',
  categories = [],
  deal,
  offers = [],
  quickOrderHref = '/quick-shop',
  catalogueHref = '/catalogue',
}: TradeCounterHomeProps) {
  const plumbingImages = [
    '/demo/kitchen-sinks--taps.jpg',
    '/demo/building-fixings.jpg',
    '/demo/tools-hand-tools.jpg',
    '/demo/lighting-hero.jpg',
  ];
  const departmentLabels = [
    'Natural gas & LPG', 'Plumbing supplies', 'Pipe, tube & fittings',
    'Heating & ventilation', 'Testing equipment', 'Tools & equipment',
    'Electrical', 'Air conditioning',
  ];
  const departments = departmentLabels.map((label, index) => ({
    label,
    href: `/categories?department=${encodeURIComponent(label.toLowerCase().replaceAll(' ', '-'))}`,
    image: plumbingImages[index % plumbingImages.length],
  }));

  return (
    <section className={styles.root} aria-label="Trade catalogue highlights">
      <div className={styles.utilityBar}>
        <span><strong>15,000+</strong> products held in stock</span>
        <span><strong>Order by 6pm</strong> for next-working-day delivery</span>
        <span><strong>4.8/5</strong> verified customer rating</span>
      </div>

      <div className={styles.heroGrid}>
        <nav className={styles.categoryRail} aria-label="Browse product departments">
          <strong className={styles.railTitle}>Browse products</strong>
          {departments.map((category) => (
            <PortalLink key={category.href} href={category.href} className={styles.railLink}>
              <span>{category.label}</span><span aria-hidden>›</span>
            </PortalLink>
          ))}
          <PortalLink href="/categories" className={styles.railAll}>View all departments</PortalLink>
        </nav>

        <div className={styles.hero} style={({ '--trade-hero': `url(${plumbingImages[0]})` } as CSSProperties)}>
          <div className={styles.heroCopy}>
            <span className={styles.eyebrow}>Trade stock, without the wait</span>
            <h1>{title}</h1>
            <p>{description}</p>
            <PortalLink href={heroHref} className={styles.primaryCta}>{heroCta}</PortalLink>
          </div>
        </div>

        {deal ? (
          <article className={styles.deal}>
            <div className={styles.dealHeading}>
              <span>Deal of the week</span>
              {deal.saving ? <strong>{deal.saving}</strong> : null}
            </div>
            <img src={plumbingImages[3]} alt="" className={styles.dealImage} />
            <div className={styles.dealBody}>
              <span className={styles.eyebrow}>{deal.eyebrow}</span>
              <h2>{deal.title}</h2>
              <div className={styles.priceRow}>
                {deal.previousPrice ? <del>{deal.previousPrice}</del> : null}
                {deal.price ? <strong>{deal.price}</strong> : null}
                <small>inc. VAT</small>
              </div>
              <PortalLink href={deal.href} className={styles.dealLink}>View deal</PortalLink>
            </div>
          </article>
        ) : null}
      </div>

      <div className={styles.actionStrip}>
        <div><strong>Know the product code?</strong><span>Add multiple lines in one go.</span></div>
        <PortalLink href={quickOrderHref} className={styles.darkCta}>Quick shop</PortalLink>
        <PortalLink href={catalogueHref} className={styles.textCta}>Request a catalogue</PortalLink>
      </div>

      {offers.length > 0 ? (
        <div className={styles.offerSection}>
          <div className={styles.sectionHeading}>
            <div><span className={styles.eyebrow}>Current promotions</span><h2>More value on everyday lines</h2></div>
            <PortalLink href="/promotions" className={styles.textCta}>View all offers</PortalLink>
          </div>
          <div className={styles.offerGrid}>
            {offers.slice(0, 3).map((offer, index) => (
              <PortalLink key={offer.href} href={offer.href} className={styles.offerCard}>
                <img src={plumbingImages[index % plumbingImages.length]} alt="" />
                <div><span>{offer.eyebrow}</span><h3>{offer.title}</h3><strong>{offer.price}</strong></div>
              </PortalLink>
            ))}
          </div>
        </div>
      ) : null}

      <div className={styles.categorySection}>
        <div className={styles.sectionHeading}><div><span className={styles.eyebrow}>The full range</span><h2>Shop by department</h2></div></div>
        <div className={styles.categoryGrid}>
          {departments.map((category) => (
            <PortalLink key={category.href} href={category.href} className={styles.categoryCard}>
              {category.image ? <img src={category.image} alt="" /> : null}
              <strong>{category.label}</strong>
            </PortalLink>
          ))}
        </div>
      </div>
    </section>
  );
}